import { useState } from "react";
import { Button, controlToastState } from "risa-oasis-ui_v2";

import { firebaseAuth } from "../../../../api/firebase/firestoreService";
import { updateBigQueryData } from "../../../../api/postCall/updateBigQueryData";
import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { saveCallingHistoryLog } from "../../../../pages/callingWorklist/services/callingHistoryService";
import { logError } from "../../../../utils/customLogger";
import {
  buildCallingFormDataFromOrder,
  CallingFormData,
  getCallingPocSelectOptions,
  getCaseStatusSelectOptions,
  getResponseStatusSelectOptions,
} from "../../../sidePanel/submissionSummarySidePanel/utils/buildCallingFormDataFromOrder";
import { convertFormDateToStorageFormat } from "../../../sidePanel/submissionSummarySidePanel/utils/convertFormDateToStorageFormat";
import CallSummaryFields from "../components/callSummaryFields";
import NonEpaTabBar from "../components/nonEpaTabBar";
import { buildHistoryLogPayload } from "../utils/buildHistoryLogPayload";
import {
  CallSummaryMode,
  CALL_SUMMARY_MODE_TABS,
} from "../utils/callSummaryMode";
import {
  extractDatePart,
  getCurrentEstTimestamp,
} from "../utils/getCurrentEstTimestamp";
import { uploadFaxDocument } from "../utils/uploadFaxDocument";

interface CallSummaryPaneProps {
  data: PharmaStpFileModel;
  /** Active channel tab, owned by the modal so it can size the dialog. */
  mode: CallSummaryMode;
  onModeChange: (mode: CallSummaryMode) => void;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Fixed Second STP Outcome label (e.g. "Appeals" on Case Appeals). When set,
   * the field stays disabled and save uses this value instead of Call/Fax.
   */
  secondStpOutcomeLabel?: string;
}

/**
 * Splits the footer evenly between Cancel and Save. The oasis `Button` takes no
 * className, so the rendered element is stretched from its wrapper.
 */
const FOOTER_BUTTON_WRAPPER_CLASSES = "flex-1 [&>button]:w-full";

/**
 * Right pane of the non-EPA modal. Owns the form state shared by the
 * Call and Fax tabs, validates it, and persists the summary to the Firestore
 * calling history plus the BigQuery order row.
 *
 * @param props.data - Order row the modal was opened for
 * @param props.onClose - Closes the modal
 * @param props.onSuccess - Called after a successful save so the table refetches
 */
const CallSummaryPane = (props: CallSummaryPaneProps) => {
  const { data, mode } = props;
  const callingReason = data?.non_epa_reason ?? "";

  const [isLoading, setIsLoading] = useState(false);
  const [dateTime] = useState(() => getCurrentEstTimestamp());
  const [formData, setFormData] = useState<CallingFormData>(() =>
    buildCallingFormDataFromOrder(data),
  );
  const [faxDocument, setFaxDocument] = useState<File | null>(null);

  const pocOptions = getCallingPocSelectOptions(formData.callingPoc);
  const responseStatusOptions = getResponseStatusSelectOptions(
    formData.responseStatus,
  );
  const caseStatusOptions = getCaseStatusSelectOptions(formData.caseStatus);

  const handleChange = (field: keyof CallingFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFaxMode = mode === CallSummaryMode.FAX;
  const isContactValid = isFaxMode
    ? formData.faxNumber.trim() !== "" && formData.sentTo.trim() !== ""
    : formData.phoneNumber.trim() !== "" && formData.repName.trim() !== "";

  // Every field is required except Next Action and Follow Up Date. Second STP
  // Outcome is read-only and always set from the tab, so there is nothing to
  // check for it.
  const isFormValid =
    isContactValid &&
    formData.caseId.trim() !== "" &&
    formData.comment.trim() !== "" &&
    formData.responseStatus.trim() !== "" &&
    formData.callingPoc.trim() !== "" &&
    formData.caseStatus.trim() !== "" &&
    (!isFaxMode || faxDocument !== null);

  const handleSave = async () => {
    const identifier = data?.identifier;
    if (!identifier) {
      controlToastState("submission-summary-save-error");
      return;
    }

    setIsLoading(true);
    const calledDate = extractDatePart(dateTime);

    try {
      const attachment =
        isFaxMode && faxDocument
          ? await uploadFaxDocument(faxDocument, identifier)
          : null;

      await saveCallingHistoryLog(
        identifier,
        buildHistoryLogPayload({
          mode,
          formData,
          dateTime,
          loggedDate: calledDate,
          reason: callingReason,
          secondStpStatus: data?.second_stp_status ?? "",
          createdBy: firebaseAuth.currentUser?.email ?? "unknown",
          attachment,
        }),
      );

      const secondStpStatus =
        props.secondStpOutcomeLabel?.trim() || mode;

      const updateData: Record<string, string> = {
        response_status: formData.responseStatus,
        // Second STP Outcome: fixed label when provided (e.g. Appeals), else Call/Fax tab.
        second_stp_status: secondStpStatus,
        non_epa_status: formData.caseStatus,
        non_epa_case_id: formData.caseId,
        non_epa_poc: formData.callingPoc,
        non_epa_call_date: convertFormDateToStorageFormat(calledDate),
        non_epa_comment: formData.comment,
        non_epa_reachout_number: isFaxMode
          ? formData.faxNumber
          : formData.phoneNumber,
      };

      if (formData.followUpDate.trim()) {
        updateData.non_epa_follow_up_date = convertFormDateToStorageFormat(
          formData.followUpDate,
        );
      }

      if (!isFaxMode) {
        updateData.non_epa_insurance_rep = formData.repName;
      }

      await updateBigQueryData({
        filters: { identifier },
        update_data: updateData,
      });

      controlToastState("submission-summary-save-success");
      props.onSuccess?.();
      props.onClose();
    } catch (error) {
      logError(error as Error, "Error saving calling summary");
      controlToastState("submission-summary-save-error");
    }
    setIsLoading(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <NonEpaTabBar
        tabs={CALL_SUMMARY_MODE_TABS}
        activeTabId={mode}
        onTabChange={(tabId) => props.onModeChange(tabId as CallSummaryMode)}
      />

      <div className="min-h-0 flex-1 p-4">
        <CallSummaryFields
          key={`${mode}-${props.secondStpOutcomeLabel ?? ""}`}
          mode={mode}
          formData={formData}
          dateTime={dateTime}
          secondStpOutcomeLabel={props.secondStpOutcomeLabel}
          pocOptions={pocOptions}
          responseStatusOptions={responseStatusOptions}
          caseStatusOptions={caseStatusOptions}
          faxDocument={faxDocument}
          onFaxDocumentChange={setFaxDocument}
          isSaving={isLoading}
          onChange={handleChange}
        />
      </div>

      <div className="flex w-full shrink-0 items-center gap-4 border-t border-primaryGray-15 px-6 py-4">
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="secondary"
            onClick={props.onClose}
            disabled={isLoading}
            size="medium"
          >
            Cancel
          </Button>
        </div>
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="primary"
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            size="medium"
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallSummaryPane;
